import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, Save } from "lucide-react";
import { Button } from "@fox1ck-org/ui";
import { DimensionFilterOp } from "@gen/tracker/v1/reporting_pb";

import { PhoneReport } from "@/components/reports/phone-report";
import { CsvButton, ReportEngineView, useReportEngine } from "@/components/reports/report-engine";
import { SaveReportDialog } from "@/components/reports/saved-views";
import { useIsPhone } from "@/lib/hooks/use-phone";
import { useT } from "@/lib/i18n";
import type { CardContext } from "./card";

/** The board's columns for one campaign (доска CReport). */
const CARD_METRICS = ["clicks", "lp_ctr", "leads", "cr", "outcome", "approve_rate", "cost", "revenue", "cpa", "profit", "roi"];

/**
 * Отчёт кампании (доска CReport): the one report engine of «Отчёты», pinned to
 * this campaign — its campaigns are this one, «Кампания: эта» is a locked
 * filter and not a level. «Открыть в Отчётах» is the same query there with
 * the pin as an ordinary filter; «Сохранить как отчёт» saves it to «Отчёты».
 * On a phone (доска MCampaign) the same engine draws its first level as a list.
 */
export function ReportTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const navigate = useNavigate();
  const id = ctx.campaign.id;
  const pinned = useMemo(() => [{ key: "campaign", op: DimensionFilterOp.IN, values: [id] }], [id]);
  const streamIds = useMemo(() => [id], [id]);
  const engine = useReportEngine({ defaults: { dims: ["creative", "landing"], metrics: CARD_METRICS }, pinned, streamIds, hiddenDims: ["campaign"] });
  const [saveOpen, setSaveOpen] = useState(false);
  const phone = useIsPhone();
  if (phone) return <PhoneReport engine={engine} starts={false} />;
  return (
    <>
      <ReportEngineView
        engine={engine}
        note={t("rep.card.note")}
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to={engine.reportsHref()}>
                <ExternalLink className="size-4" strokeWidth={1.5} />
                {t("rep.card.openInReports")}
              </Link>
            </Button>
            <Button variant="secondary" onClick={() => setSaveOpen(true)}>
              <Save className="size-4" strokeWidth={1.5} />
              {t("rep.card.saveAsReport")}
            </Button>
            <CsvButton engine={engine} stem={`campaign-${id.slice(0, 8)}`} />
          </>
        }
      />
      <SaveReportDialog engine={engine} open={saveOpen} onOpenChange={setSaveOpen} withPinned nameHint={ctx.campaign.name} onSaved={(v) => navigate(`/stats?view=${encodeURIComponent(v.id)}`)} />
    </>
  );
}
